import React, { useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Menu, Plus, ExternalLink } from 'lucide-react';
import colors from 'theme/colors';
import CategoriesTab from './tabs/CategoriesTab';
import ProductsTab from './tabs/ProductsTab';
import OptionGroupsTab from './tabs/OptionGroupsTab';
import { useUser } from 'context/user/User';
import useToast from 'hooks/useToast';
import ToastContainer from 'components/_UI/ToastsContainer';

const TABS = [
    { key: 'categories',    label: 'Categorias',       createLabel: 'Nova categoria' },
    { key: 'products',      label: 'Produtos',         createLabel: 'Novo produto' },
    { key: 'option-groups', label: 'Grupos de Opções',  createLabel: 'Novo grupo' },
];

export default function RestaurantMenuPage() {
    const [activeTab, setActiveTab] = useState('categories');
    const { user } = useUser();
    const slug = user?.establishments?.[0]?.slug;
    const { toasts, toast } = useToast();
    const tabRefs = useRef({});
    const tabComponentRefs = useRef({});

    const handleTabKeyDown = useCallback((e) => {
        const currentIndex = TABS.findIndex(t => t.key === activeTab);
        let nextIndex = currentIndex;

        if (e.key === 'ArrowRight') {
            nextIndex = (currentIndex + 1) % TABS.length;
        } else if (e.key === 'ArrowLeft') {
            nextIndex = (currentIndex - 1 + TABS.length) % TABS.length;
        } else if (e.key === 'Home') {
            nextIndex = 0;
        } else if (e.key === 'End') {
            nextIndex = TABS.length - 1;
        } else {
            return;
        }

        e.preventDefault();
        const nextKey = TABS[nextIndex].key;
        setActiveTab(nextKey);
        tabRefs.current[nextKey]?.focus();
    }, [activeTab]);

    const activeTabConfig = TABS.find(t => t.key === activeTab);

    function handleCreateClick() {
        tabComponentRefs.current[activeTab]?.openCreate();
    }

    if (!slug) {
        return (
            <PageWrapper>
                <NoEstablishmentState>
                    <NoEstablishmentIcon><Menu size={36} color='#999' /></NoEstablishmentIcon>
                    <NoEstablishmentTitle>Nenhum estabelecimento encontrado</NoEstablishmentTitle>
                    <NoEstablishmentHint>Crie um estabelecimento para gerenciar seu cardápio digital.</NoEstablishmentHint>
                </NoEstablishmentState>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper>
            <ToastContainer toasts={toasts} />

            <PageHeader>
                <HeaderLeft>
                    <HeaderIcon><Menu size={20} color={colors.occasionalPurple} /></HeaderIcon>
                    <div>
                        <PageTitle>Cardápio Digital</PageTitle>
                        <PageSubtitle>Gerencie categorias, produtos e opções do seu cardápio</PageSubtitle>
                    </div>
                </HeaderLeft>
                <HeaderActions>
                    <MenuLinkBtn
                        as="a"
                        href={`/${slug}/menu`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Ver cardápio do cliente"
                    >
                        <ExternalLink size={18} strokeWidth={2} />
                    </MenuLinkBtn>
                    <NewBtn onClick={handleCreateClick} aria-label={activeTabConfig?.createLabel}>
                        <Plus size={20} color='#fff' strokeWidth={2.5} />
                    </NewBtn>
                </HeaderActions>
            </PageHeader>

            <TableCard>
                <TabBar role='tablist' aria-label='Seções do cardápio'>
                    {TABS.map(tab => (
                        <TabButton
                            key={tab.key}
                            ref={el => { tabRefs.current[tab.key] = el; }}
                            role='tab'
                            id={`tab-${tab.key}`}
                            aria-selected={activeTab === tab.key}
                            aria-controls={`panel-${tab.key}`}
                            tabIndex={activeTab === tab.key ? 0 : -1}
                            $active={activeTab === tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            onKeyDown={handleTabKeyDown}
                        >
                            {tab.label}
                        </TabButton>
                    ))}
                </TabBar>

                <TabContent
                    role='tabpanel'
                    id={`panel-${activeTab}`}
                    aria-labelledby={`tab-${activeTab}`}
                >
                    <TabPanel $visible={activeTab === 'categories'}>
                        <CategoriesTab
                            ref={el => { tabComponentRefs.current['categories'] = el; }}
                            slug={slug}
                            toast={toast}
                            onCategoriesChange={cats => tabComponentRefs.current['products']?.updateCategories(cats)}
                        />
                    </TabPanel>
                    <TabPanel $visible={activeTab === 'products'}>
                        <ProductsTab ref={el => { tabComponentRefs.current['products'] = el; }} slug={slug} toast={toast} />
                    </TabPanel>
                    <TabPanel $visible={activeTab === 'option-groups'}>
                        <OptionGroupsTab ref={el => { tabComponentRefs.current['option-groups'] = el; }} slug={slug} toast={toast} />
                    </TabPanel>
                </TabContent>
            </TableCard>
        </PageWrapper>
    );
}

const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${colors.neutralBackground};
`;

const PageHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 16px 14px;
    border-radius: 14px 14px 0 0;
    background: ${colors.neutralLight};
    flex-shrink: 0;
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const MenuLinkBtn = styled.button`
    width: 44px;
    height: 44px;
    border-radius: 14px;
    background: ${colors.neutralDark};
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${colors.night};
    text-decoration: none;
    transition: background 0.15s;
    &:hover { background: #e0e0e5; }
`;

const NewBtn = styled.button`
    width: 44px;
    height: 44px;
    border-radius: 14px;
    background: ${colors.occasionalPurple};
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transition: transform 0.15s, box-shadow 0.15s;
    &:hover { transform: scale(1.06); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2); }
    &:active { transform: scale(0.96); }
    &:focus-visible { outline: 2px solid ${colors.occasionalPurple}; outline-offset: 3px; }
`;

const HeaderIcon = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${colors.occasionalPurple}10;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const PageTitle = styled.h1`
    font-size: 18px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0;
    line-height: 1.3;
`;

const PageSubtitle = styled.p`
    font-size: 13px;
    color: #666;
    margin: 2px 0 0;
`;

const TableCard = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 0 0 14px 14px;
    box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.04), 0 2px 12px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    min-height: 0;
`;

const TabBar = styled.div`
    display: flex;
    flex-shrink: 0;
    overflow-x: auto;
    padding: 0 16px;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const TabButton = styled.button`
    padding: 13px 20px;
    font-size: 14px;
    font-weight: ${({ $active }) => $active ? '700' : '500'};
    color: ${({ $active }) => $active ? colors.occasionalPurple : '#666'};
    background: none;
    border: none;
    border-bottom: 3px solid ${({ $active }) => $active ? colors.occasionalPurple : 'transparent'};
    margin-bottom: -1px;
    cursor: pointer;
    white-space: nowrap;
    transition: color 0.15s, border-color 0.15s;
    &:hover { color: ${colors.occasionalPurple}; }
    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: -2px;
        border-radius: 4px 4px 0 0;
    }
`;

const TabContent = styled.div`
    flex: 1;
    overflow-y: auto;
    position: relative;
    background: #fff;
`;

const TabPanel = styled.div`
    display: ${({ $visible }) => $visible ? 'block' : 'none'};
    height: 100%;
`;

const NoEstablishmentState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 40px 20px;
    text-align: center;
`;

const NoEstablishmentIcon = styled.div`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: ${colors.neutralDark};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
`;

const NoEstablishmentTitle = styled.h2`
    font-size: 17px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0 0 6px;
`;

const NoEstablishmentHint = styled.p`
    font-size: 14px;
    color: #666;
    margin: 0;
    max-width: 280px;
`;
